import React, { useMemo } from 'react';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { Box, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { MarketWarningVendor } from '../../services/marketWarnings';

const VENDOR_NAMES: Record<MarketWarningVendor, string> = {
  upbit: 'Upbit',
  bithumb: 'Bithumb',
};

export interface MarketWarningBadgeProps {
  vendors: readonly MarketWarningVendor[];
}

export const MarketWarningBadge: React.FC<MarketWarningBadgeProps> = ({
  vendors,
}) => {
  const { t } = useTranslation();
  const venueNames = useMemo(
    () =>
      (['upbit', 'bithumb'] as const)
        .filter((vendor) => vendors.includes(vendor))
        .map((vendor) => VENDOR_NAMES[vendor])
        .join(', '),
    [vendors],
  );

  if (!venueNames) return null;
  const label = t('marketWarnings.accessibleLabel', { venues: venueNames });

  return (
    <Tooltip title={t('marketWarnings.tooltip', { venues: venueNames })} arrow>
      <Box
        component="span"
        role="img"
        aria-label={label}
        data-testid="market-warning-badge"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'warning.main',
          lineHeight: 1,
          flexShrink: 0,
        }}
      >
        <WarningAmberRoundedIcon sx={{ fontSize: '1rem' }} />
      </Box>
    </Tooltip>
  );
};

export default MarketWarningBadge;
